import { IWPItem, WPItem } from "@/loader";

export type MediaType = "image" | "video" | "text" | "application" | "audio";

export interface IWPMediaSize {
	file?: string;
	width?: number;
	height?: number;
	mime_type?: string;
	source_url?: string;
}

export interface IWPMediaSizes {
	medium?: IWPMediaSize | null;
	thumbnail?: IWPMediaSize | null;
	full?: IWPMediaSize;
	[size: string]: IWPMediaSize | null | undefined;
}

export function loadWPMediaSize(data: IWPMediaSize | null = {}): IWPMediaSize {
	if (!data) {
		data = {};
	}
	return {
		file: String.orDefault(data.file),
		width: Number.orDefault(data.width),
		height: Number.orDefault(data.height),
		mime_type: String.orDefault(data.mime_type),
		source_url: String.orDefault(data.source_url),
	};
}

export interface IWPMedia extends IWPItem {
	description?: {
		raw?: string;
		rendered?: string;
	};
	caption?: {
		raw?: string;
		rendered?: string;
	};
	alt_text?: string;
	media_type?: MediaType;
	mime_type?: string;
	media_details?: {
		width?: number;
		height?: number;
		file?: string;
		sizes?: IWPMediaSizes;
		image_meta?: {
			aperture?: string;
			credit?: string;
			camera?: string;
			caption?: string;
			created_timestamp?: string;
			copyright?: string;
			focal_length?: string;
			iso?: string;
			shutter_speed?: string;
			title?: string;
			orientation?: string;
			keywords?: string[];
		};
	};
	post?: number;
	source_url?: string;
	missing_image_sizes?: string[];
}

export class WPMedia extends WPItem implements IWPMedia {
	public description!: {
		raw?: string;
		rendered?: string;
	};
	public caption!: {
		raw?: string;
		rendered?: string;
	};
	public alt_text!: string;
	public media_type!: MediaType;
	public mime_type!: string;
	public media_details!: {
		width: number;
		height: number;
		file: string;
		sizes: IWPMediaSizes;
		image_meta: {
			aperture: string;
			credit: string;
			camera: string;
			caption: string;
			created_timestamp: string;
			copyright: string;
			focal_length: string;
			iso: string;
			shutter_speed: string;
			title: string;
			orientation: string;
			keywords: string[];
		};
	};
	public post!: number;
	public source_url!: string;
	public missing_image_sizes!: string[];

	constructor(data: IWPMedia = {}) {
		super();
		this.load(data);
	}

	public load(data: IWPMedia) {
		super.load(data);
		this.alt_text = String.orDefault(data.alt_text);
		this.media_type = data.media_type || "text";
		this.mime_type = String.orDefault(data.mime_type);
		this.post = Number.orDefault(data.post);
		this.source_url = String.orDefault(data.source_url);
		this.missing_image_sizes = Array.isArray(data.missing_image_sizes) ? data.missing_image_sizes.map(x => String.orDefault(x)) : [];
		this.description = {
			raw: String.orDefault(data.description?.raw),
			rendered: String.orDefault(data.description?.rendered),
		};
		this.caption = {
			raw: String.orDefault(data.caption?.raw),
			rendered: String.orDefault(data.caption?.rendered),
		};
		this.media_details = {
			width: Number.orDefault(data.media_details?.width),
			height: Number.orDefault(data.media_details?.height),
			file: String.orDefault(data.media_details?.file),
			sizes: {
				medium: null,
				thumbnail: null,
				full: loadWPMediaSize(),
			},
			image_meta: {
				aperture: String.orDefault(data.media_details?.image_meta?.aperture),
				credit: String.orDefault(data.media_details?.image_meta?.credit),
				camera: String.orDefault(data.media_details?.image_meta?.camera),
				caption: String.orDefault(data.media_details?.image_meta?.caption),
				created_timestamp: String.orDefault(data.media_details?.image_meta?.created_timestamp),
				copyright: String.orDefault(data.media_details?.image_meta?.copyright),
				focal_length: String.orDefault(data.media_details?.image_meta?.focal_length),
				iso: String.orDefault(data.media_details?.image_meta?.iso),
				shutter_speed: String.orDefault(data.media_details?.image_meta?.shutter_speed),
				title: String.orDefault(data.media_details?.image_meta?.title),
				orientation: String.orDefault(data.media_details?.image_meta?.orientation),
				// @ts-ignore
				keywords: Array.isArray(data.media_details?.image_meta?.keywords) ? data.media_details.image_meta.keywords.map(x => String.orDefault(x)) : [],
			},
		};
		if (data.media_details?.sizes) {
			let sizes: IWPMediaSizes = { ...this.media_details.sizes };
			for (let prop in data.media_details.sizes) {
				sizes[prop] = loadWPMediaSize(data.media_details.sizes[prop]);
			}
			this.media_details.sizes = sizes;
		}
	}

	public update(data: IWPMedia) {
		super.update(data);
		if (data.alt_text !== undefined) {
			this.alt_text = String.orDefault(data.alt_text);
		}
		if (data.media_type !== undefined) {
			this.media_type = data.media_type || "text";
		}
		if (data.mime_type !== undefined) {
			this.mime_type = String.orDefault(data.mime_type);
		}
		if (data.post !== undefined) {
			this.post = Number.orDefault(data.post);
		}
		if (data.source_url !== undefined) {
			this.source_url = String.orDefault(data.source_url);
		}
		if (data.missing_image_sizes !== undefined) {
			this.missing_image_sizes = Array.isArray(data.missing_image_sizes) ? data.missing_image_sizes.map(x => String.orDefault(x)) : [];
		}
		if (data.description !== undefined) {
			if (data.description.raw !== undefined) {
				this.description.raw = String.orDefault(data.description?.raw);
			}
			if (data.description.rendered !== undefined) {
				this.description.rendered = String.orDefault(data.description?.rendered);
			}
		}
		if (data.caption !== undefined) {
			if (data.caption.raw !== undefined) {
				this.caption.raw = String.orDefault(data.caption?.raw);
			}
			if (data.caption.rendered !== undefined) {
				this.caption.rendered = String.orDefault(data.caption?.rendered);
			}
		}
		if (data.media_details !== undefined) {
			if (data.media_details.width !== undefined) {
				this.media_details.width = Number.orDefault(data.media_details?.width);
			}
			if (data.media_details.height !== undefined) {
				this.media_details.height = Number.orDefault(data.media_details?.height);
			}
			if (data.media_details.file !== undefined) {
				this.media_details.file = String.orDefault(data.media_details?.file);
			}
			if (data.media_details.sizes !== undefined) {
				let sizes: IWPMediaSizes = { ...this.media_details.sizes };
				for (let prop in data.media_details.sizes) {
					sizes[prop] = loadWPMediaSize(data.media_details.sizes[prop]);
				}
				this.media_details.sizes = sizes;
			}
			if (data.media_details.image_meta !== undefined) {
				if (data.media_details.image_meta.aperture !== undefined) {
					this.media_details.image_meta.aperture = String.orDefault(data.media_details?.image_meta?.aperture);
				}
				if (data.media_details.image_meta.credit !== undefined) {
					this.media_details.image_meta.credit = String.orDefault(data.media_details?.image_meta?.credit);
				}
				if (data.media_details.image_meta.camera !== undefined) {
					this.media_details.image_meta.camera = String.orDefault(data.media_details?.image_meta?.camera);
				}
				if (data.media_details.image_meta.caption !== undefined) {
					this.media_details.image_meta.caption = String.orDefault(data.media_details?.image_meta?.caption);
				}
				if (data.media_details.image_meta.created_timestamp !== undefined) {
					this.media_details.image_meta.created_timestamp = String.orDefault(data.media_details?.image_meta?.created_timestamp);
				}
				if (data.media_details.image_meta.copyright !== undefined) {
					this.media_details.image_meta.copyright = String.orDefault(data.media_details?.image_meta?.copyright);
				}
				if (data.media_details.image_meta.focal_length !== undefined) {
					this.media_details.image_meta.focal_length = String.orDefault(data.media_details?.image_meta?.focal_length);
				}
				if (data.media_details.image_meta.iso !== undefined) {
					this.media_details.image_meta.iso = String.orDefault(data.media_details?.image_meta?.iso);
				}
				if (data.media_details.image_meta.shutter_speed !== undefined) {
					this.media_details.image_meta.shutter_speed = String.orDefault(data.media_details?.image_meta?.shutter_speed);
				}
				if (data.media_details.image_meta.title !== undefined) {
					this.media_details.image_meta.title = String.orDefault(data.media_details?.image_meta?.title);
				}
				if (data.media_details.image_meta.orientation !== undefined) {
					this.media_details.image_meta.orientation = String.orDefault(data.media_details?.image_meta?.orientation);
				}
				if (data.media_details.image_meta.keywords !== undefined) {
					this.media_details.image_meta.keywords = Array.isArray(data.media_details?.image_meta?.keywords) ? data.media_details.image_meta.keywords.map(x => String.orDefault(x)) : [];
				}
			}
		}
	}

	public restData() {
		let data = super.restData();
		return {
			...data,
			alt_text: this.alt_text,
			caption: this.caption.raw,
			description: this.description.raw,
			post: this.post,
		};
	}
}
